import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Criterion, CriterionInput } from '../api';

/**
 * The epic's definition of done: the criteria, and how many are met.
 *
 * Two modes on purpose. Ticking a box is a one-click act and saves immediately — putting
 * it behind Edit → Save would make recording "we did that" a three-click chore, and it
 * would stop being recorded. Changing the WORDING is a different kind of act, so that is
 * an explicit edit with a Cancel.
 *
 * Criteria are replaced as a whole list under the version guard, which is why every
 * existing `id` is carried back: without it the server treats the entry as new and the
 * "met on / met by" stamp on every untouched criterion is lost.
 */

const toInput = (criterion: Criterion): CriterionInput => ({
  id: criterion.id,
  text: criterion.text,
  met: criterion.met,
});

function Progress({ met, total }: { met: number; total: number }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 180 }}>
      <LinearProgress
        variant="determinate"
        // An epic with no criteria reads as 0%, never as complete: `0/0` is "nobody has
        // said what done means", which is the opposite of done.
        value={total === 0 ? 0 : (met / total) * 100}
        color={total > 0 && met === total ? 'success' : 'primary'}
        sx={{ flex: 1, height: 6, borderRadius: 3 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {met} / {total} met
      </Typography>
    </Stack>
  );
}

/** Editing the WORDING, which is a deliberate act with a Cancel — unlike ticking a box. */
function CriteriaEditor({
  draft,
  setDraft,
  saving,
  onCancel,
  onSave,
}: {
  draft: CriterionInput[];
  setDraft: (next: CriterionInput[]) => void;
  saving: boolean;
  onCancel: () => void;
  onSave: (next: CriterionInput[]) => Promise<void>;
}) {
  const set = (index: number, text: string) =>
    setDraft(draft.map((entry, i) => (i === index ? { ...entry, text } : entry)));

  return (
    <Stack spacing={1.5}>
      {draft.map((entry, index) => (
        <Stack key={entry.id ?? `new-${index}`} direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            value={entry.text}
            placeholder="How will we know this epic is done?"
            onChange={(event) => set(index, event.target.value)}
          />
          <Tooltip title="Remove">
            <IconButton
              size="small"
              aria-label={`Remove criterion ${index + 1}`}
              onClick={() => setDraft(draft.filter((_, i) => i !== index))}
            >
              ×
            </IconButton>
          </Tooltip>
        </Stack>
      ))}

      <Stack direction="row" spacing={1}>
        <Button size="small" onClick={() => setDraft([...draft, { text: '' }])}>
          Add criterion
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={saving}
          // Blank rows are dropped rather than rejected: an empty box is how someone
          // abandons a criterion they started typing, and the API refuses empty text.
          onClick={() => void onSave(draft.filter((entry) => entry.text.trim()))}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

export function EpicCriteria({
  criteria,
  saving,
  onSave,
}: {
  criteria: Criterion[];
  saving: boolean;
  onSave: (next: CriterionInput[]) => Promise<void>;
}) {
  const [draft, setDraft] = useState<CriterionInput[] | null>(null);
  const met = criteria.filter((criterion) => criterion.met).length;

  const tick = (criterion: Criterion, met: boolean) =>
    void onSave(criteria.map((c) => (c.id === criterion.id ? { ...toInput(c), met } : toInput(c))));

  if (draft !== null) {
    return (
      <CriteriaEditor
        draft={draft}
        setDraft={setDraft}
        saving={saving}
        onCancel={() => setDraft(null)}
        onSave={async (next) => {
          await onSave(next);
          setDraft(null);
        }}
      />
    );
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Progress met={met} total={criteria.length} />
        <Button size="small" onClick={() => setDraft(criteria.map(toInput))}>
          {criteria.length ? 'Edit criteria' : 'Add criteria'}
        </Button>
      </Stack>

      {criteria.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No success criteria yet — nothing here says when this epic is finished.
        </Typography>
      )}

      {criteria.map((criterion) => (
        <Stack key={criterion.id} direction="row" spacing={1} alignItems="flex-start">
          <Checkbox
            size="small"
            checked={criterion.met}
            disabled={saving}
            onChange={(event) => tick(criterion, event.target.checked)}
            inputProps={{ 'aria-label': criterion.text }}
            sx={{ mt: -0.5 }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{ textDecoration: criterion.met ? 'line-through' : undefined }}
            >
              {criterion.text}
            </Typography>
            {criterion.met && criterion.metAt && (
              <Typography variant="caption" color="text.secondary">
                met {new Date(criterion.metAt).toLocaleDateString()}
                {criterion.metBy ? ` · ${criterion.metBy}` : ''}
              </Typography>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}
